import './TableProfits.styles.scss';

import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { OverlayPanel } from 'primereact/overlaypanel';
import React, { FC, useEffect, useRef, useState } from 'react';

import { useOverlayClickOutside } from '../../hooks/useOverlayClickOutside';
import { PlayerProfit, TableProfit } from '../../model/TableProfit';
import commonStore from '../../store/CommonStore';
import tableStore from '../../store/TableStore';
import { formatNumber } from '../Game/table.render.utils';
import { TableProfitsProps } from './TableProfits.props';

export const TableProfits: FC<TableProfitsProps> = ({
  tableId,
  toggleEvent,
}) => {
  const overlayRef = useRef<OverlayPanel>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [profit, setProfit] = useState<TableProfit>();
  const [onTablePlayersPids, setOnTablePlayersPids] = useState<Set<string>>(
    new Set(),
  );
  const [tableBB, setTableBB] = useState<number>(0);

  useOverlayClickOutside(overlayRef);

  useEffect(() => {
    if (isOpen) {
      tableStore.findActiveTableById(tableId).then(table => {
        setOnTablePlayersPids(
          new Set(table.players.map(player => player?.pid)),
        );
        setTableBB(table.bb);
      });
    } else {
      setProfit(undefined);
      setOnTablePlayersPids(new Set());
      setTableBB(0);
    }
  }, [tableId, isOpen]);

  useEffect(() => {
    if (overlayRef.current && toggleEvent) {
      overlayRef.current.toggle(toggleEvent);
    } else if (overlayRef.current) {
      overlayRef.current.hide();
    }
  }, [toggleEvent]);

  useEffect(() => {
    if (overlayRef.current && isOpen) {
      tableStore.loadTableProfit(tableId).then(responseProfit => {
        responseProfit?.players?.sort((playerProfit1, playerProfit2) => {
          const onTable1 = onTablePlayersPids.has(playerProfit1.pid) ? 0 : 1;
          const onTable2 = onTablePlayersPids.has(playerProfit2.pid) ? 0 : 1;
          if (onTable1 !== onTable2) return onTable1 - onTable2;
          return playerProfit1.nick.localeCompare(playerProfit2.nick);
        });
        setProfit(responseProfit);
      });
    }
  }, [isOpen, tableId, onTablePlayersPids]);

  const playerTemplate = (player: PlayerProfit): React.ReactNode => (
    <span>
      {player.nick} (<b>{player.pid}</b>)
    </span>
  );

  const getConvertedMoney = (value: number): string =>
    formatNumber(commonStore.convertMoney(value, tableBB));

  const getPlayerColor = (profit: number): string => {
    if (profit > 0) return '#03ee03';
    if (profit < 0) return 'red';
    return '#ffff02';
  };

  const profitTemplate = (player: PlayerProfit): React.ReactNode => {
    const sign = player.profit > 0 ? '+' : '';
    return (
      <span style={{ color: getPlayerColor(player.profit) }}>
        {sign}
        {getConvertedMoney(player.profit)}
        {commonStore.getIsChipsConvertedToBlinds() && (
          <span className="bb-text">{' BB'}</span>
        )}
      </span>
    );
  };

  return (
    <OverlayPanel
      onShow={() => setIsOpen(true)}
      onHide={() => setIsOpen(false)}
      ref={overlayRef}
      showCloseIcon
      className="TableProfits-Component mt-2"
      dismissable={false}
      data-ignore-outside-click
    >
      <div className="header p-2">
        <h3 className="m-0">Profits</h3>
      </div>
      {profit && isOpen && (
        <DataTable
          rowClassName={player =>
            onTablePlayersPids.has(player.pid) ? '' : 'absent-player'
          }
          value={profit?.players}
          scrollable
          scrollHeight="610px"
          emptyMessage="No any profit yet"
        >
          <Column
            header="Player"
            body={playerTemplate}
            style={{ wordBreak: 'break-all' }}
          />
          <Column
            header="Profit"
            sortable
            sortField="profit"
            style={{ whiteSpace: 'nowrap' }}
            body={profitTemplate}
          />
        </DataTable>
      )}
    </OverlayPanel>
  );
};
